import * as THREE from 'three';

export function canvasTexture(
  width: number,
  height: number,
  draw: (ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement) => void,
  repeat = false,
): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D indisponível');
  draw(ctx, canvas);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  if (repeat) {
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
  }
  texture.anisotropy = 4;
  texture.needsUpdate = true;
  return texture;
}

export function makeAsphaltTexture(): THREE.CanvasTexture {
  return canvasTexture(512, 512, (ctx) => {
    ctx.fillStyle = '#3c4148';
    ctx.fillRect(0, 0, 512, 512);
    const image = ctx.getImageData(0, 0, 512, 512);
    for (let i = 0; i < image.data.length; i += 4) {
      const n = (Math.random() - 0.5) * 22;
      image.data[i] = clampByte(image.data[i] + n);
      image.data[i + 1] = clampByte(image.data[i + 1] + n);
      image.data[i + 2] = clampByte(image.data[i + 2] + n - 2);
    }
    ctx.putImageData(image, 0, 0);
    ctx.strokeStyle = 'rgba(0,0,0,0.18)';
    ctx.lineWidth = 1;
    for (let i = 0; i < 18; i += 1) {
      ctx.beginPath();
      let x = Math.random() * 512;
      let y = Math.random() * 512;
      ctx.moveTo(x, y);
      for (let k = 0; k < 5; k += 1) {
        x += (Math.random() - 0.5) * 80;
        y += (Math.random() - 0.5) * 40;
        ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
  }, true);
}

export function makeWindowTexture(): THREE.CanvasTexture {
  return canvasTexture(256, 256, (ctx) => {
    ctx.fillStyle = '#232a33';
    ctx.fillRect(0, 0, 256, 256);
    for (let y = 10; y < 250; y += 28) {
      for (let x = 8; x < 250; x += 22) {
        const lit = Math.random() > 0.42;
        ctx.fillStyle = lit ? (Math.random() > 0.55 ? '#f0ddb0' : '#c5d4e4') : '#12171e';
        ctx.fillRect(x, y, 12, 16);
      }
    }
  }, true);
}

export function makeLabelTexture(text: string, background: string, foreground: string): THREE.CanvasTexture {
  return canvasTexture(512, 128, (ctx) => {
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, 512, 128);
    ctx.fillStyle = foreground;
    ctx.font = '700 72px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, 256, 68);
  });
}

export function makeCheckerTexture(): THREE.CanvasTexture {
  return canvasTexture(128, 128, (ctx) => {
    const cell = 16;
    for (let y = 0; y < 8; y += 1) {
      for (let x = 0; x < 8; x += 1) {
        ctx.fillStyle = (x + y) % 2 === 0 ? '#f4f4f4' : '#161616';
        ctx.fillRect(x * cell, y * cell, cell, cell);
      }
    }
  }, true);
}

function clampByte(value: number): number {
  return Math.max(0, Math.min(255, value));
}

export function makeSkyMaterial(top: number, bottom: number, ground: number): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    fog: false,
    uniforms: {
      top: { value: new THREE.Color(top) },
      bottom: { value: new THREE.Color(bottom) },
      ground: { value: new THREE.Color(ground) },
    },
    vertexShader: `
      varying vec3 vPos;
      void main() {
        vPos = position;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      varying vec3 vPos;
      uniform vec3 top;
      uniform vec3 bottom;
      uniform vec3 ground;
      void main() {
        float h = normalize(vPos).y;
        vec3 col = mix(bottom, top, smoothstep(-0.02, 0.65, h));
        if (h < 0.0) col = mix(ground, bottom, smoothstep(-0.45, 0.0, h));
        gl_FragColor = vec4(col, 1.0);
      }
    `,
  });
}
